import React, { useEffect, useState } from "react";


// Helper to group news by month
function groupByMonth(news) {
  const grouped = {};
  news.forEach((item) => {
    const [year, month] = item.date.split("-");
    const key = `${year}-${month}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  });
  return grouped;
}

function formatMonth(key) {
  const [year, month] = key.split("-");
  return `${new Date(year, month - 1).toLocaleString("default", {
    month: "long",
  })} ${year}`;
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatContent(content) {
  if (!content) return null;
  const lines = content.split("\n").filter((line) => line.trim() !== "");
  return (
    <div>
      {lines.map((line, idx) => {
        // Auto-link URLs
        const parts = line.split(/(https?:\/\/[^\s]+)/g);
        const linkedLine = parts.map((part, i) =>
          /^https?:\/\//.test(part) ? (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#3a4edc" }}
            >
              {part}
            </a>
          ) : (
            part
          )
        );
        // Bold the first line
        return (
          <div
            key={idx}
            style={
              idx === 0
                ? { fontWeight: "bold", marginBottom: "0.5em" }
                : { marginBottom: "0.5em" }
            }
          >
            {linkedLine}
          </div>
        );
      })}
    </div>
  );
}

function News() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIds, setOpenIds] = useState({});

  useEffect(() => {
    fetch("https://wyrowingbackend.onrender.com/news")
      .then((res) => res.json())
      .then((data) => {
        setNews(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading news:", err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    // Re-render the Facebook plugin after navigation/render
    const interval = setInterval(() => {
      if (window.FB && window.FB.XFBML) {
        window.FB.XFBML.parse();
        clearInterval(interval);
      }
    }, 300);
    return () => clearInterval(interval);
  }, []);

  // Separate Top Stories and Other News
  const topStories = news.filter((item) =>
    item.title.toLowerCase().includes("top")
  );
  const otherNews = news.filter(
    (item) => !item.title.toLowerCase().includes("top")
  );

  const grouped = groupByMonth(otherNews);

  const toggleOpen = (id) => {
    setOpenIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="centered-page">
      <h2 style={{ textAlign: "left" }}>News Feed</h2>
      {loading ? (
        <p>Loading news...</p>
      ) : news.length === 0 ? (
        <p>No news available at this time.</p>
      ) : (
        <>
          {/* Top Stories Section */}
          {topStories.length > 0 && (
            <div style={{ marginBottom: "2.5rem" }}>
              <h3
                style={{
                  fontWeight: "bold",
                  fontSize: "1.3rem",
                  color: "#162278",
                }}
              >
                Top Stories
              </h3>
              <ul style={{ listStyle: "none", padding: 0 }}>
                {topStories
                  .slice()
                  .sort((a, b) => b.date.localeCompare(a.date))
                  .map((item) => (
                    <li
                      key={item.id}
                      style={{
                        marginBottom: "1.5rem",
                        borderLeft: "4px solid #162278",
                        paddingLeft: "1rem",
                        background: "#ffffff",
                        borderRadius: "4px",
                        cursor: "pointer",
                        transition: "background 0.2s",
                      }}
                      onClick={() => toggleOpen(item.id)}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                            flex: 1,
                          }}
                        >
                          {item.title
                            .replace("Top", "")
                            .replace("Story", "")
                            .trim()}
                        </span>
                        <span
                          className="news-date"
                          style={{
                            color: "#555",
                            fontSize: "0.85rem",
                            marginLeft: "1rem",
                          }}
                        >
                          {formatDate(item.date)}
                        </span>
                        <span
                          style={{
                            marginLeft: "1rem",
                            fontSize: "1.2rem",
                            color: "#162278",
                          }}
                        >
                          {openIds[item.id] ? "▲" : "▼"}
                        </span>
                      </div>
                      {openIds[item.id] && (
                        <div
                          style={{
                            margin: "0.75rem 0 0.5rem 0",
                            color: "#333",
                            background: "#ffffff",
                            padding: "0.75rem",
                            borderRadius: "6px",
                            fontSize: "1rem",
                            maxHeight: "40em",
                            overflow: "auto",
                          }}
                        >
                          {formatContent(item.content || item.summary)}
                          <div style={{ marginTop: "1em" }}>
                            <a
                              href={item.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#ffffff",
                                textDecoration: "underline",
                                fontWeight: "bold",
                              }}
                            >
                              View Full Document
                            </a>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
          )}



          {/* Regular News Section */}
          {Object.keys(grouped)
            .sort((a, b) => b.localeCompare(a)) // newest month first
            .map((monthKey) => (
              <div key={monthKey} style={{ marginTop: "2.5rem" }}>
                <h3
                  style={{
                    fontWeight: "bold",
                    fontSize: "1.2rem",
                    textAlign: "left",
                    color: "#162278",
                  }}
                >
                  {formatMonth(monthKey)}
                </h3>
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {grouped[monthKey]
                    .slice()
                    .sort((a, b) => b.date.localeCompare(a.date)) // sort by date descending
                    .map((item) => (
                      <li
                        key={item.id}
                        style={{
                          marginBottom: "1.5rem",
                          borderLeft: "4px solid #162278",
                          paddingLeft: "1rem",
                          background: "#f8f9fc",
                          borderRadius: "4px",
                          cursor: "pointer",
                          transition: "background 0.2s",
                        }}
                        onClick={() => toggleOpen(item.id)}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: "bold",
                              fontSize: "1.1rem",
                              flex: 1,
                            }}
                          >
                            {item.title}
                          </span>
                          <span
                            className="news-date"
                            style={{
                              color: "#555",
                              fontSize: "0.95rem",
                              marginLeft: "1rem",
                            }}
                          >
                            {formatDate(item.date)}
                          </span>
                          <span
                            style={{
                              marginLeft: "1rem",
                              fontSize: "1.2rem",
                              color: "#162278",
                            }}
                          >
                            {openIds[item.id] ? "▲" : "▼"}
                          </span>
                        </div>
                        {openIds[item.id] && (
                          <div
                            style={{
                              margin: "0.75rem 0 0.5rem 0",
                              color: "#333",
                              background: "#f4f6fa",
                              padding: "0.75rem",
                              borderRadius: "6px",
                              fontSize: "1rem",
                              maxHeight: "40em",
                              overflow: "auto",
                            }}
                          >
                            {formatContent(item.content || item.summary)}
                            <div style={{ marginTop: "1em" }}>
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "#3a4edc",
                                  textDecoration: "underline",
                                  fontWeight: "bold",
                                }}
                              >
                                View Full Document
                              </a>
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                </ul>
              </div>
            ))}
        </>
      )}
    </div>
  );
}

export default News;
